import { useCallback } from 'react';

import { Typography, Button } from '@aircall/tractor';
import { useLazyQuery } from '@apollo/client';
import { Banner } from '@components/Banner';
import { useToast } from '@dashboard/hooks';
import { ResendUserInvitationEmailQuery } from '@generated/ResendUserInvitationEmailQuery';
import { RESENT_USER_INVITATION_EMAIL_QUERY } from '@graphql/queries/ResendUserInvitationEmail';
import noop from 'lodash-es/noop';
import { useTranslation } from 'react-i18next';

interface ResendEmailBannerProps {
  userId: string;
}

export function ResendEmailBanner({ userId }: ResendEmailBannerProps) {
  const { t } = useTranslation();
  const toast = useToast();

  const [resendInvitation] = useLazyQuery<ResendUserInvitationEmailQuery>(
    RESENT_USER_INVITATION_EMAIL_QUERY,
    {
      fetchPolicy: 'no-cache',
      onCompleted: () => {
        toast.show({
          variant: 'success',
          message: t('user_profile.resend_email_banner.message.success'),
        });
      },
      onError: () => {
        toast.show({
          variant: 'error',
          message: t('user_profile.resend_email_banner.message.error'),
        });
      },
    }
  );

  const handleSendEmail = useCallback(() => {
    resendInvitation({ variables: { userId, bodyBuilder: noop } });
  }, [resendInvitation, userId]);

  return (
    <Banner
      type='notification'
      text={t('user_profile.resend_email_banner.message.notification')}
      data-test='resend-email-notification-banner'
    >
      <Button
        mode='link'
        variant='black'
        size='xSmall'
        onClick={handleSendEmail}
        data-test='resend-email-notification-banner-button'
      >
        <Typography variant='body' color='text.base'>
          {t('user_profile.resend_email_banner.link')}
        </Typography>
      </Button>
    </Banner>
  );
}
